import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ordersAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Package, 
  LogOut, 
  Trash2, 
  Eye,
  ShoppingBag,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const statusLabels = {
  new: 'Новый',
  processing: 'В работе',
  completed: 'Выполнен',
  cancelled: 'Отменен',
};

const statusColors = {
  new: 'bg-blue-100 text-blue-700',
  processing: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

const AdminOrdersPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/admin/login');
      return;
    }
    loadOrders();
  }, [user, navigate]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const data = await ordersAPI.getAll();
      setOrders(data);
    } catch (error) {
      toast.error('Ошибка загрузки заказов');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await ordersAPI.updateStatus(orderId, newStatus);
      toast.success('Статус заказа обновлен');
      loadOrders();
    } catch (error) {
      toast.error('Ошибка обновления статуса');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот заказ?')) {
      return;
    }
    
    try {
      await ordersAPI.delete(orderId);
      toast.success('Заказ удален');
      loadOrders();
    } catch (error) {
      toast.error('Ошибка удаления заказа');
    }
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowDetails(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold text-gray-900">Админ-панель</h1>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="border-emerald-600 text-emerald-700"
                  onClick={() => navigate('/admin/orders')}
                >
                  <Package className="w-4 h-4 mr-2" />
                  Заказы
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/admin/products')}
                >
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Товары
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/admin/gallery')}
                >
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Галерея
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/admin/reviews')}
                >
                  <Package className="w-4 h-4 mr-2" />
                  Отзывы
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                {user?.username}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={logout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Выйти
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Заказы</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={loadOrders}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Обновить
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-emerald-600 mb-4" />
                <p className="text-gray-600">Загрузка заказов...</p>
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">Заказов пока нет</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>№</TableHead>
                      <TableHead>Дата</TableHead>
                      <TableHead>Клиент</TableHead>
                      <TableHead>Телефон</TableHead>
                      <TableHead>Город</TableHead>
                      <TableHead>Сумма</TableHead>
                      <TableHead>Статус</TableHead>
                      <TableHead className="text-right">Действия</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((order, index) => (
                      <TableRow key={order.id}>
                        <TableCell className="font-medium">#{index + 1}</TableCell>
                        <TableCell className="text-sm">
                          {formatDate(order.created_at)}
                        </TableCell>
                        <TableCell>{order.customer.fullName}</TableCell>
                        <TableCell>{order.customer.phone}</TableCell>
                        <TableCell>{order.customer.city}</TableCell>
                        <TableCell className="font-semibold">
                          {order.total.toLocaleString('ru-RU')} ₽
                        </TableCell>
                        <TableCell>
                          <Select
                            value={order.status}
                            onValueChange={(value) => handleStatusChange(order.id, value)}
                          >
                            <SelectTrigger className="w-[140px]">
                              <Badge className={statusColors[order.status]}>
                                {statusLabels[order.status]}
                              </Badge>
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(statusLabels).map(([value, label]) => (
                                <SelectItem key={value} value={value}>
                                  {label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewDetails(order)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteOrder(order.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Order Details Modal */}
      {showDetails && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Детали заказа</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Customer Info */}
              <div>
                <h3 className="font-semibold mb-3">Информация о клиенте</h3>
                <div className="space-y-2 text-sm">
                  <p><strong>ФИО:</strong> {selectedOrder.customer.fullName}</p>
                  <p><strong>Телефон:</strong> {selectedOrder.customer.phone}</p>
                  <p><strong>Email:</strong> {selectedOrder.customer.email}</p>
                  <p><strong>Город:</strong> {selectedOrder.customer.city}</p>
                  <p><strong>Адрес:</strong> {selectedOrder.customer.address}</p>
                  <p><strong>Способ оплаты:</strong> {selectedOrder.customer.paymentMethod === 'cash' ? 'Наличные' : 'Картой'}</p>
                  {selectedOrder.customer.comment && (
                    <p><strong>Комментарий:</strong> {selectedOrder.customer.comment}</p>
                  )}
                </div>
              </div>

              {/* Order Items */}
              <div>
                <h3 className="font-semibold mb-3">Товары</h3>
                <div className="space-y-3">
                  {selectedOrder.items.map((item, idx) => (
                    <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <p className="font-medium">{item.name}</p>
                          <p className="text-sm text-gray-600">
                            Размер: {item.size.width}x{item.size.length} м
                          </p>
                          <p className="text-sm text-gray-600">
                            Количество: {item.quantity} шт.
                          </p>
                          {item.selectedPolycarbonate && (
                            <p className="text-sm text-emerald-600 font-medium">
                              Поликарбонат: {item.selectedPolycarbonate}
                            </p>
                          )}
                          {/* Выбранные опции */}
                          {item.selectedOptions && item.selectedOptions.length > 0 && (
                            <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
                              <p className="font-semibold text-blue-800 mb-1">Выбранные опции:</p>
                              {item.selectedOptions.map((opt, idx) => (
                                <p key={idx} className="text-blue-700">
                                  • {opt.name}: {opt.price > 0 ? `+${opt.price.toLocaleString('ru-RU')} ₽` : 'Включено'}
                                </p>
                              ))}
                            </div>
                          )}
                          {item.options && !item.selectedOptions && (
                            <div className="mt-2 p-2 bg-amber-50 rounded text-xs">
                              <p className="font-semibold text-amber-800 mb-1">Опции:</p>
                              {item.options.polycarbonate && <p className="text-amber-700">• Поликарбонат: {item.options.polycarbonate}</p>}
                              {item.options.additionalOptions && item.options.additionalOptions.map((opt, idx) => (
                                <p key={idx} className="text-amber-700">• {opt.name}</p>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-semibold">
                            {(item.price * item.quantity).toLocaleString('ru-RU')} ₽
                          </p>
                          <p className="text-sm text-gray-600">
                            {item.price.toLocaleString('ru-RU')} ₽ × {item.quantity}
                          </p>
                        </div>
                      </div>
                      
                      {/* Детали из калькулятора */}
                      {item.calculatorConfig && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-xs font-semibold text-emerald-700 mb-2">Конфигурация из калькулятора:</p>
                          <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                            {item.calculatorConfig.type && (
                              <p><strong>Тип:</strong> {item.calculatorConfig.type}</p>
                            )}
                            {item.calculatorConfig.frame && (
                              <p><strong>Каркас:</strong> {item.calculatorConfig.frame}</p>
                            )}
                            {item.calculatorConfig.polycarbonate && (
                              <p className="col-span-2"><strong>Поликарбонат:</strong> {item.calculatorConfig.polycarbonate}</p>
                            )}
                            {item.calculatorConfig.options && (
                              <>
                                <p className="col-span-2 font-semibold mt-2">Дополнительные опции:</p>
                                {Object.entries(item.calculatorConfig.options).map(([key, value]) => (
                                  value === 'Да' && (
                                    <p key={key} className="col-span-2">
                                      • {key === 'fortochka' ? 'Форточка' : 
                                         key === 'termoprivod' ? 'Термопривод' :
                                         key === 'fundament' ? 'Фундамент' :
                                         key === 'montazh' ? 'Монтаж' : key}: {value}
                                    </p>
                                  )
                                ))}
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Total */}
              <div className="flex justify-between items-center pt-4 border-t">
                <span className="text-lg font-semibold">Итого:</span>
                <span className="text-2xl font-bold text-emerald-600">
                  {selectedOrder.total.toLocaleString('ru-RU')} ₽
                </span>
              </div>

              <Button
                onClick={() => setShowDetails(false)}
                className="w-full"
              >
                Закрыть
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminOrdersPage;
